import { ArrowBackIos, Save } from '@mui/icons-material';
import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { Tooltip } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { SelectButton } from 'primereact/selectbutton';
import { AutoComplete } from 'primereact/autocomplete';
import { toast } from 'react-toastify';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { Header } from '../../../../components/header/header';
import {
  ErrorText,
  StyledHeaderContainer,
  StyledTitleHeader
} from '../../../../components/layout/main/mainStyles';
import Strings from '../../../../i18n';
import {
  useAddCustomFirmwareVersionMutation,
  useAddFirmwareVersionMutation,
  useEditCustomFirmwareVersionMutation,
  useEditFirmwareVersionMutation,
  useGetDetailsCustomFirmwareVersionMutation,
  useGetFirmwareVersionDetailsMutation
} from '../../../../services/firmwareVersions/firmwareVersionsService';
import { addEditFirmwareVersionValidator } from '../../../../utils/validators';
import { StyledInputLabel } from '../../versionTemplateStyles';
import { ButtonIconStyled } from '../../../locations/allLocationsList/allLocationListStyles';
import { TooltipStyled } from '../../../organizations/organizationsLocs/organizationsLocStyles';
import { useGetLocationsMutation } from '../../../../services/locations/locationsService';
import { useGetDeviceByLocationMutation } from '../../../../services/deviceByLocation/deviceByLocationService';

export const AddEditCustomVersionForm = () => {
  const navigate = useNavigate();
  const { versionId, customVersionId } = useParams();
  const [isEditing, setIsEditing] = useState(false);
  const items = [
    { name: 'Development', value: false },
    { name: 'Production', value: true }
  ];
  const [itemsAutoComplete, setItemsAutoComplete] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState<{
    label: string;
    value: number;
  }>();
  const [selectedDevice, setSelectedDevice] = useState<string>();
  const [firmwareVersion, setFirmwareVersion] = useState<{
    name: string;
    isProduction: boolean;
    active: boolean;
    minWifiModuleVersionName: string;
    id?: number;
    locationId?: any;
    deviceId?: string;
  }>({
    name: '',
    minWifiModuleVersionName: '',
    isProduction: false,
    active: true,
    locationId: undefined,
    deviceId: undefined
  });

  const [
    getFirmwareVersionDetails,
    { isSuccess: successGetData, data: dataFirmwareVersion }
  ] = useGetDetailsCustomFirmwareVersionMutation();
  const [
    getLocations,
    { isSuccess: successGetLocations, data: dataLocations }
  ] = useGetLocationsMutation();

  const [getDevices, { isSuccess: successGetDevices, data: dataDevices }] =
    useGetDeviceByLocationMutation();

  const [
    postFirmwareVersion,
    { isSuccess: successPost, data: successPostData }
  ] = useAddCustomFirmwareVersionMutation();

  const [editFirmwareVersion, { isSuccess: successEdit, data: dataEdit }] =
    useEditCustomFirmwareVersionMutation();

  useEffect(() => {
    getLocations('');
  }, []);

  useEffect(() => {
    if (customVersionId) {
      getFirmwareVersionDetails(customVersionId);
      setIsEditing(true);
    }
  }, [customVersionId, getFirmwareVersionDetails]);

  useEffect(() => {
    if (successGetData && dataFirmwareVersion && dataLocations) {
      setFirmwareVersion({
        name: dataFirmwareVersion.name,
        isProduction: dataFirmwareVersion.isProduction,
        active: dataFirmwareVersion.active,
        locationId: dataFirmwareVersion.locationId,
        minWifiModuleVersionName: '',
        deviceId: dataFirmwareVersion.deviceId
      });
      setSelectedLocation({
        label:
          dataLocations?.find(
            (loc: any) => loc.id === dataFirmwareVersion.locationId
          )?.name ?? '',
        value: dataFirmwareVersion.locationId
      });
      setSelectedDevice(dataFirmwareVersion.deviceId);
    }
  }, [successGetData, dataFirmwareVersion, successGetLocations, dataLocations]);

  useEffect(() => {
    if (successGetLocations && dataLocations) {
      const formattedLocations = dataLocations.map((loc: any) => ({
        label: loc.name,
        value: loc.id
      }));
      setItemsAutoComplete(formattedLocations as any);
    }
  }, [successGetLocations, dataLocations]);

  const save = () => {
    setFirmwareVersion({
      ...firmwareVersion,
      locationId: selectedLocation?.value,
      deviceId: selectedDevice || undefined
    });
    document.getElementById('submitForm')?.click();
  };

  const sendInfo = (values: {
    name: string;
    minWifiModuleVersionName: string;
  }) => {
    if (!isEditing) {
      const payload = {
        ...values,
        isProduction: firmwareVersion.isProduction,
        active: firmwareVersion.active,
        locationId: firmwareVersion.locationId,
        deviceId: firmwareVersion.deviceId,
        firmwareVersionId: versionId
      };
      postFirmwareVersion(payload);
    } else {
      const payload = {
        ...values,
        isProduction: firmwareVersion.isProduction,
        active: firmwareVersion.active,
        locationId: firmwareVersion.locationId,
        deviceId: firmwareVersion.deviceId,
        id: customVersionId
      };
      if (customVersionId) {
        editFirmwareVersion(payload);
      }
    }
  };

  useEffect(() => {
    if (successPost && successPostData) {
      toast.success(Strings.versions.successAddFirmwareVersion);
      navigate(
        `/firmwareVersions/${versionId}/customVersions/${successPostData}/files`
      );
    }
  }, [successPost]);

  useEffect(() => {
    if (successEdit) {
      toast.success(Strings.versions.successEditFirmwareVersion);
      navigate(
        `/firmwareVersions/${versionId}/customVersions/${customVersionId}/files`
      );
    }
  }, [successEdit]);

  const search = (event: any) => {
    if (dataLocations) {
      const filteredLocations = dataLocations
        .map((loc: any) => ({
          label: loc.name,
          value: loc.id
        }))
        .filter((location: any) =>
          location.label.toLowerCase().includes(event.query.toLowerCase())
        );
      setItemsAutoComplete(filteredLocations as any);
    }
  };

  useEffect(() => {
    if (selectedLocation?.value) {
      getDevices(selectedLocation.value);
    }
  }, [selectedLocation]);

  return (
    <div>
      <Header>
        <StyledHeaderContainer>
          <div className="row justify-content-between">
            <div className="col-4 text-left">
              <ButtonIconStyled size="small" onClick={() => navigate(-1)}>
                <ArrowBackIos />
              </ButtonIconStyled>
            </div>
            <div className="col-4 text-center">
              <StyledTitleHeader>
                {Strings.serverAccess.menu.versions}
              </StyledTitleHeader>
            </div>
            <div className="col-4 text-right">
              <Tooltip
                title={Strings.common.save}
                placement="top"
                arrow
                componentsProps={TooltipStyled}
              >
                <ButtonIconStyled size="small" onClick={save}>
                  <Save />
                </ButtonIconStyled>
              </Tooltip>
            </div>
          </div>
        </StyledHeaderContainer>
      </Header>
      <div className="row justify-content-center">
        <div className="col-11 col-10">
          <Formik
            enableReinitialize
            validationSchema={addEditFirmwareVersionValidator}
            initialValues={{
              name: firmwareVersion.name,
              minWifiModuleVersionName: firmwareVersion.minWifiModuleVersionName
            }}
            onSubmit={(values) => {
              sendInfo(values);
            }}
          >
            {({
              handleChange,
              handleSubmit,
              values,
              isValid,
              setFieldTouched,
              errors,
              touched
            }) => (
              <div>
                <div>
                  <StyledInputLabel>
                    {Strings.versions.versionName}
                  </StyledInputLabel>
                  <InputText
                    type="text"
                    id="name"
                    name="name"
                    value={values.name}
                    onChange={handleChange('name')}
                    onBlur={() => setFieldTouched('name')}
                    className="w-100"
                    disabled={isEditing}
                  />
                  {errors.name && touched.name && (
                    <ErrorText>{errors.name}</ErrorText>
                  )}
                </div>
                <div className="mt-4">
                  <StyledInputLabel>
                    {Strings.versions.targetLocation}
                  </StyledInputLabel>
                  <AutoComplete
                    value={selectedLocation}
                    disabled={isEditing}
                    suggestions={itemsAutoComplete}
                    completeMethod={search}
                    onChange={(e) => {
                      setSelectedLocation(e.value);
                    }}
                    field="label"
                  />
                </div>
                <div className="mt-4">
                  <StyledInputLabel>
                    {Strings.versions.targetDevice} ({Strings.common.optional})
                  </StyledInputLabel>
                  <Dropdown
                    disabled={isEditing}
                    options={dataDevices}
                    value={selectedDevice}
                    optionLabel="deviceId"
                    optionValue="deviceId"
                    onChange={(e) => {
                      setSelectedDevice(e.value);
                    }}
                  />
                </div>
                <div className="mt-3">
                  <SelectButton
                    value={firmwareVersion.isProduction}
                    onChange={(e) => {
                      setFirmwareVersion({
                        ...firmwareVersion,
                        isProduction: e.value
                      });
                    }}
                    options={items}
                    optionLabel="name"
                  />
                </div>
                <div className="mt-3">
                  <Checkbox
                    name="activeFw"
                    value={firmwareVersion.active}
                    onChange={(e) => {
                      setFirmwareVersion({
                        ...firmwareVersion,
                        active: e.checked as boolean
                      });
                    }}
                    checked={firmwareVersion.active}
                  />
                  <span className="ml-2">Make active</span>
                </div>

                <div>
                  <button
                    type="submit"
                    onClick={() => handleSubmit()}
                    id="submitForm"
                    className="d-none"
                  >
                    {Strings.common.save}
                  </button>
                </div>
              </div>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};
